import { isImmutable, List } from 'immutable';

import { isTruthy } from '@/utils/bool';
import { getSize } from '@/utils/iterable/getSize';

import { CURRENCIES, ERROR } from '@/constants/product';
import { COMBINATIONS } from '@/constants/variations';
import { ATTRIBUTE } from '@/constants/attributes';
import { FORM } from '@/constants/forms';
import { EMPTY } from '@/constants';

export function reduceErrors(errors, toBool = true) {
  function recursiveReduce(result, item) {
    return result || (
      isImmutable(item)
        ? item.reduce(recursiveReduce, result)
        : toBool
          ? !!item
          : item
    );
  }

  return isImmutable(errors)
    ? errors.reduce(recursiveReduce, false)
    : errors;
}

function getProductErrorPath({ key, path }) {
  if (typeof path === 'string' || typeof path === 'number') {
    return [path, key, ERROR];
  } else if (Array.isArray(path)) {
    return [...path, key, ERROR];
  } else {
    return [key, ERROR];
  }
}

export function clearErrors({ errors = [], path, product }) {
  function reduceErrorKeys(data, key) {
    let result = data;

    if (result.has(ERROR)) {
      result = result.deleteIn([ERROR, key]);
    }

    return result.deleteIn(getProductErrorPath({ key, path }));
  }

  return errors.reduce(reduceErrorKeys, product);
}

export function setErrors({ errors = [], path, product }) {
  function reduceErrorKeys(data, value, key) {
    let result = data;

    if (result.has(ERROR)) {
      result = result.setIn([ERROR, key], value);
    }

    return result.setIn(getProductErrorPath({ key, path }), value);
  }

  return errors.reduce(reduceErrorKeys, product);
}

export function clearVariationsCombinationErrors({ errors = [], combinationIndex, product }) {
  function getInternationalPriceErrors() {
    function reduceCurrenciesToEmptyErrors(result, currency) {
      return result.set(currency, false);
    }

    return CURRENCIES[product.get('channel')].reduce(reduceCurrenciesToEmptyErrors, EMPTY.MAP);
  }

  function reduceErrorKeys(data, key) {
    switch (key) {
      case ATTRIBUTE.RETAIL_PRICE:
      case ATTRIBUTE.WHOLESALE_PRICE: {
        let result = data;
        let attributeErrors = result.getIn([FORM.VARIATIONS, ERROR, key]);

        if (!List.isList(attributeErrors)) {
          attributeErrors = result.getIn([FORM.VARIATIONS, COMBINATIONS]).map(getInternationalPriceErrors);
        }

        if (combinationIndex !== undefined && attributeErrors.get(combinationIndex)) {
          attributeErrors = attributeErrors.set(combinationIndex, getInternationalPriceErrors());
        }

        if (result.has(ERROR)) {
          if (!getSize(attributeErrors.filter(isTruthy))) {
            result = result.deleteIn([ERROR, FORM.VARIATIONS, key]);
          }

          if (result.hasIn([ERROR, FORM.VARIATIONS]) && !getSize(result.getIn([ERROR, FORM.VARIATIONS]))) {
            result = result.deleteIn([ERROR, FORM.VARIATIONS]);
          }
        }

        return result.setIn([FORM.VARIATIONS, ERROR, key], attributeErrors);
      }

      default: {
        let result = data;
        let attributeErrors = result.getIn([FORM.VARIATIONS, ERROR, key]);

        if (!List.isList(attributeErrors)) {
          attributeErrors = List(Array(getSize(result.getIn([FORM.VARIATIONS, COMBINATIONS]))).fill(false));
        }

        if (combinationIndex !== undefined && attributeErrors.get(combinationIndex)) {
          attributeErrors = attributeErrors.set(combinationIndex, false);
        }

        if (result.has(ERROR)) {
          if (!getSize(attributeErrors.filter(isTruthy))) {
            result = result.deleteIn([ERROR, FORM.VARIATIONS, key]);
          }

          if (result.hasIn([ERROR, FORM.VARIATIONS]) && !getSize(result.getIn([ERROR, FORM.VARIATIONS]))) {
            result = result.deleteIn([ERROR, FORM.VARIATIONS]);
          }
        }

        return result.setIn([FORM.VARIATIONS, ERROR, key], attributeErrors);
      }
    }
  }

  return errors.reduce(reduceErrorKeys, product);
}

export function removeErrors(errors) {
  function mapErrors(error) {
    return isImmutable(error)
      ? removeErrors(error)
      : false;
  }

  return errors.map(mapErrors);
}
